import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { Loader } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import KanbanBoard from "@/components/KanbanBoard";
import GanttChart from "@/components/GanttChart";
import TaskDialog from "@/components/TaskDialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, LayoutGrid, GanttChartSquare, Wallet, Trash2, Info } from "lucide-react";
import { toast } from "sonner";

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [settings, setSettings] = useState({});
  const [taskOpen, setTaskOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [expOpen, setExpOpen] = useState(false);
  const [exp, setExp] = useState({ libelle: "", categorie: "autre", montant: 0, date: "" });

  const load = () => api.get(`/projects/${id}`).then((r) => setP(r.data));
  useEffect(() => { load(); api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {}); api.get("/settings").then((r) => setSettings(r.data)); }, [id]);

  if (!p) return <Loader />;

  const move = async (tid, statut) => {
    setP((prev) => ({ ...prev, tasks: prev.tasks.map((t) => (t.id === tid ? { ...t, statut } : t)) }));
    try { await api.patch(`/tasks/${tid}/status`, { statut }); } catch (e) { toast.error(apiErr(e)); load(); }
  };
  const addExpense = async (e) => {
    e.preventDefault();
    try { await api.post(`/projects/${id}/expenses`, exp); toast.success("Dépense ajoutée"); setExpOpen(false); setExp({ libelle: "", categorie: "autre", montant: 0, date: "" }); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const rmExpense = async (eid) => { await api.delete(`/projects/${id}/expenses/${eid}`); load(); };

  const pctBudget = p.budget_prevu ? Math.round((p.budget_consomme / p.budget_prevu) * 100) : 0;

  return (
    <div>
      <button onClick={() => nav("/projets")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" />Retour aux projets</button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <p className="font-mono text-xs text-slate-400">{p.numero}</p>
          <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{p.nom}</h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <StatusBadge value={p.statut} /><StatusBadge value={p.categorie} /><StatusBadge value={p.priorite} />
          </div>
        </div>
        <Button onClick={() => { setEditingTask(null); setTaskOpen(true); }} data-testid="add-project-task" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvelle tâche</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rounded-xl border bg-white p-4"><p className="text-xs text-slate-400">Avancement</p><p className="font-mono text-xl font-bold mt-1">{p.avancement}%</p><Progress value={p.avancement} className="h-1.5 mt-2" /></div>
        <div className="rounded-xl border bg-white p-4"><p className="text-xs text-slate-400">Budget prévu</p><p className="font-mono text-xl font-bold mt-1">{fmtMoney(p.budget_prevu)}</p></div>
        <div className="rounded-xl border bg-white p-4"><p className="text-xs text-slate-400">Consommé ({pctBudget}%)</p><p className={`font-mono text-xl font-bold mt-1 ${pctBudget > 100 ? "text-rose-600" : ""}`}>{fmtMoney(p.budget_consomme)}</p></div>
        <div className="rounded-xl border bg-white p-4"><p className="text-xs text-slate-400">Restant</p><p className="font-mono text-xl font-bold mt-1">{fmtMoney(p.budget_restant)}</p></div>
      </div>

      <Tabs defaultValue="kanban">
        <TabsList className="mb-4">
          <TabsTrigger value="overview" data-testid="tab-overview"><Info className="h-4 w-4 mr-1.5" />Aperçu</TabsTrigger>
          <TabsTrigger value="kanban" data-testid="tab-project-kanban"><LayoutGrid className="h-4 w-4 mr-1.5" />Kanban</TabsTrigger>
          <TabsTrigger value="gantt" data-testid="tab-gantt"><GanttChartSquare className="h-4 w-4 mr-1.5" />Gantt</TabsTrigger>
          <TabsTrigger value="budget" data-testid="tab-budget"><Wallet className="h-4 w-4 mr-1.5" />Budget</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="rounded-xl border bg-white p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><p className="text-xs text-slate-400 mb-1">Description</p><p className="text-sm text-slate-700">{p.description || "—"}</p></div>
            <div><p className="text-xs text-slate-400 mb-1">Responsable</p><p className="text-sm font-medium">{p.responsable_name || "—"}</p></div>
            <div><p className="text-xs text-slate-400 mb-1">Date de début</p><p className="text-sm">{fmtDate(p.date_debut)}</p></div>
            <div><p className="text-xs text-slate-400 mb-1">Date de fin prévue</p><p className="text-sm">{fmtDate(p.date_fin_prevue)}</p></div>
            <div><p className="text-xs text-slate-400 mb-1">Équipe</p><p className="text-sm">{(p.equipe_names || []).join(", ") || "—"}</p></div>
          </div>
        </TabsContent>

        <TabsContent value="kanban">
          {p.tasks.length ? <KanbanBoard tasks={p.tasks} onMove={move} onOpen={(t) => { setEditingTask(t); setTaskOpen(true); }} /> :
            <p className="text-sm text-slate-400 py-8 text-center">Aucune tâche. Ajoutez-en une.</p>}
        </TabsContent>

        <TabsContent value="gantt">
          <div className="rounded-xl border bg-white p-6"><GanttChart tasks={p.tasks} /></div>
        </TabsContent>

        <TabsContent value="budget">
          <div className="flex justify-end mb-3"><Button size="sm" variant="outline" onClick={() => setExpOpen(true)} data-testid="add-expense"><Plus className="h-4 w-4 mr-1.5" />Ajouter une dépense</Button></div>
          <div className="rounded-xl border bg-white overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Libellé</TableHead><TableHead>Catégorie</TableHead><TableHead>Date</TableHead><TableHead>Montant</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {(p.expenses || []).length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Aucune dépense enregistrée</TableCell></TableRow> :
                  p.expenses.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.libelle}</TableCell>
                      <TableCell><StatusBadge value={e.categorie} /></TableCell>
                      <TableCell className="text-sm">{fmtDate(e.date)}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtMoney(e.montant)}</TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => rmExpense(e.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button></TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <TaskDialog open={taskOpen} onOpenChange={setTaskOpen} initial={editingTask} employees={employees} lockedProject={p} onSaved={load} />

      <Dialog open={expOpen} onOpenChange={setExpOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Nouvelle dépense</DialogTitle></DialogHeader>
          <form onSubmit={addExpense} className="space-y-3">
            <div><Label className="text-xs">Libellé</Label><Input value={exp.libelle} onChange={(e) => setExp({ ...exp, libelle: e.target.value })} required className="mt-1" data-testid="exp-libelle" /></div>
            <div><Label className="text-xs">Catégorie</Label>
              <Select value={exp.categorie} onValueChange={(v) => setExp({ ...exp, categorie: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{(settings.expense_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs">Montant (FCFA)</Label><Input type="number" value={exp.montant} onChange={(e) => setExp({ ...exp, montant: Number(e.target.value) })} className="mt-1" data-testid="exp-montant" /></div>
            <div><Label className="text-xs">Date</Label><Input type="date" value={exp.date} onChange={(e) => setExp({ ...exp, date: e.target.value })} className="mt-1" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setExpOpen(false)}>Annuler</Button><Button type="submit" className="bg-brand-600 text-white" data-testid="exp-submit">Ajouter</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
